// Ranking + filtering for the CPLEX Sankey search index, backed by Fuse.js.
//
// Why Fuse: hand-rolled scoring (substring + token-AND + compact-mode) covers
// the easy cases but misses typos, transpositions, and proper character-position
// weighting. Fuse uses the Bitap algorithm under the hood and gives us typo
// tolerance, multi-character substring scoring, and a tunable threshold for
// free. Greenlit on review 5d1bfe98.
//
// Architecture:
//   - We keep `normSearchKey()` as the input preprocessor — separators in the
//     entity label fold to spaces and tokens get a compact-mode shadow appended
//     so Fuse can match "safecure" against "Safe-Cure 1200" (compact form
//     "safecure1200" is part of the indexed string).
//   - One Fuse instance is created per entity list, indexed on the
//     compound `_fuseField` (spaced + compact key joined with a separator).
//   - rankEntities returns the same `{kind, results}[]` shape the dropdown
//     already consumes — no UI changes downstream.

import Fuse, { type IFuseOptions } from 'fuse.js';
import type { EntityKind, SearchEntity } from './searchIndex';
import { KIND_ORDER, normSearchKey } from './searchIndex';

export interface RankedEntity {
  entity: SearchEntity;
  score: number;
}

const MAX_PER_GROUP = 8;

// Compound text Fuse indexes per entity. Spaced + compact forms separated by
// a single space so token-level matches against either spelling succeed.
function fuseText(e: SearchEntity): string {
  const compact = e.searchKey.replace(/\s+/g, '');
  return compact === e.searchKey
    ? e.searchKey
    : `${e.searchKey} ${compact}`;
}

// Fuse tuning — threshold controls fuzziness (0 = exact, 1 = match anything).
// 0.35 is a balance that surfaces typos like "safecur" but doesn't flood with
// irrelevant noise. ignoreLocation lets a token match anywhere in the string,
// not just near the start. minMatchCharLength=2 avoids one-char queries
// matching everything containing that letter.
const FUSE_OPTIONS: IFuseOptions<SearchEntity & { _fuseField: string }> = {
  keys: ['_fuseField'],
  threshold: 0.35,
  ignoreLocation: true,
  includeScore: true,
  minMatchCharLength: 2,
  isCaseSensitive: false,
};

/**
 * Build a Fuse instance over a SearchEntity list. Exposed so other modules
 * (e.g. `buildQueryChip`) can reuse the same matcher and stay in sync with
 * the dropdown's notion of "what matches".
 */
export function createFuse(entities: readonly SearchEntity[]) {
  const indexed = entities.map((e) => ({ ...e, _fuseField: fuseText(e) }));
  return new Fuse(indexed, FUSE_OPTIONS);
}

/**
 * Score one entity against query tokens. Kept as a thin helper for unit
 * testing — runs a single-item Fuse search and returns a 0..100 score
 * (Fuse's raw score is 0=best, 1=worst; we invert + scale so the dropdown's
 * "higher is better" sort still works).
 */
export function scoreEntity(entity: SearchEntity, tokens: readonly string[]): number {
  if (tokens.length === 0) return 0;
  const fuse = createFuse([entity]);
  const hits = fuse.search(tokens.join(' '));
  if (hits.length === 0) return 0;
  const fuseScore = hits[0].score ?? 1;
  // Fuse score: 0 = perfect, 1 = no match. Invert + scale to 0..100.
  return Math.round((1 - fuseScore) * 100);
}

/**
 * Rank entities for a free-text query and group the top N per kind.
 *
 * Empty query returns no results — the caller hides the dropdown in that
 * case. Otherwise Fuse drives the ordering across the whole list and we
 * partition the results back into per-kind groups for the dropdown's
 * grouped display.
 */
export function rankEntities(
  entities: readonly SearchEntity[],
  query: string,
): { kind: EntityKind; results: RankedEntity[] }[] {
  const folded = normSearchKey(query);
  if (folded.length === 0) return [];
  const fuse = createFuse(entities);
  const hits = fuse.search(folded);
  if (hits.length === 0) return [];

  const byKind = new Map<EntityKind, RankedEntity[]>();
  for (const h of hits) {
    const entity = h.item as SearchEntity;
    const arr = byKind.get(entity.kind) ?? [];
    if (arr.length < MAX_PER_GROUP) {
      arr.push({ entity, score: Math.round((1 - (h.score ?? 1)) * 100) });
      byKind.set(entity.kind, arr);
    }
  }
  return KIND_ORDER.filter((k) => byKind.has(k)).map((k) => ({
    kind: k,
    results: byKind.get(k) ?? [],
  }));
}

/**
 * Build a synthetic "Includes: <query>" chip — the union of every entity Fuse
 * considers a match for the query. Returns null when no entity matches so the
 * caller can hide the include-all option rather than commit a dead-end chip.
 *
 * Lives alongside `rankEntities` so both paths share the same Fuse instance
 * and threshold — change Fuse tuning once, both the dropdown and the
 * Includes-chip union update together.
 */
export function buildQueryChip(
  query: string,
  entities: readonly SearchEntity[],
): SearchEntity | null {
  const folded = normSearchKey(query);
  if (folded.length === 0) return null;
  // Drop any existing synthetic `query` chips before indexing — they'd
  // otherwise let Includes:-chips chain-include each other.
  const candidates = entities.filter((e) => e.kind !== 'query');
  if (candidates.length === 0) return null;
  const fuse = createFuse(candidates);
  const hits = fuse.search(folded);
  if (hits.length === 0) return null;
  const indices = new Set<number>();
  for (const h of hits) {
    for (const i of (h.item as SearchEntity).rowIndices) indices.add(i);
  }
  if (indices.size === 0) return null;
  return {
    kind: 'query',
    value: folded,
    label: query.trim(),
    searchKey: folded,
    rowIndices: [...indices].sort((a, b) => a - b),
  };
}
